import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";

interface PartEntry {
  partNumber: string;
  partName: string;
  quantity: number;
  unitCost: number;
  totalCost: number;
  supplier?: string;
  notes?: string;
}

interface LaborEntry {
  technicianId: string;
  technicianName: string;
  hours: number;
  hourlyRate: number;
  totalCost: number;
  description: string;
}

interface EquipmentMaintenanceData {
  equipmentId: string;
  maintenanceType: "preventive" | "corrective" | "emergency";
  priority: "low" | "medium" | "high" | "critical";
  scheduledDate: string;
  actualStartDate?: string;
  actualEndDate?: string;
  assignedTechnicianId: string;
  workDescription: string;
  partsUsed: PartEntry[];
  laborEntries: LaborEntry[];
  totalPartsCost: number;
  totalLaborCost: number;
  totalCost: number;
  status: "scheduled" | "in_progress" | "completed" | "cancelled";
  completionNotes?: string;
  completedBy?: string;
  completedDate?: string;
  nextMaintenanceDate?: string;
  maintenanceHistory?: string[];
  attachments?: string[];
}

interface MaintenanceSummary {
  category: string;
  description: string;
  cost: number;
  type: "parts" | "labor";
}

class EquipmentMaintenanceWizardStore {
  rootStore: RootStore;

  // UI State
  activeStep = 0;
  loading = false;
  error: string | null = null;
  success: string | null = null;

  // Form Data
  formData: EquipmentMaintenanceData = {
    equipmentId: "",
    maintenanceType: "preventive",
    priority: "medium",
    scheduledDate: new Date().toISOString().split("T")[0],
    assignedTechnicianId: "",
    workDescription: "",
    partsUsed: [],
    laborEntries: [],
    totalPartsCost: 0,
    totalLaborCost: 0,
    totalCost: 0,
    status: "scheduled",
  };

  // Lookup Data
  maintenanceTypes: any[] = [];
  technicians: any[] = [];

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // Actions
  setActiveStep = (step: number) => {
    this.activeStep = step;
  };

  nextStep = () => {
    if (this.validateCurrentStep()) {
      this.activeStep++;
    }
  };

  prevStep = () => {
    if (this.activeStep > 0) {
      this.activeStep--;
    }
  };

  updateFormData = <K extends keyof EquipmentMaintenanceData>(
    field: K,
    value: EquipmentMaintenanceData[K]
  ) => {
    this.formData[field] = value;
  };

  addPartEntry = (part: PartEntry) => {
    this.formData.partsUsed.push(part);
    this.calculateTotalCosts();
  };

  removePartEntry = (index: number) => {
    this.formData.partsUsed.splice(index, 1);
    this.calculateTotalCosts();
  };

  updatePartEntry = (index: number, part: PartEntry) => {
    this.formData.partsUsed[index] = part;
    this.calculateTotalCosts();
  };

  addLaborEntry = (labor: LaborEntry) => {
    this.formData.laborEntries.push(labor);
    this.calculateTotalCosts();
  };

  removeLaborEntry = (index: number) => {
    this.formData.laborEntries.splice(index, 1);
    this.calculateTotalCosts();
  };

  updateLaborEntry = (index: number, labor: LaborEntry) => {
    this.formData.laborEntries[index] = labor;
    this.calculateTotalCosts();
  };

  calculateTotalCosts = () => {
    const totalPartsCost = this.formData.partsUsed.reduce(
      (sum, part) => sum + part.totalCost,
      0
    );
    const totalLaborCost = this.formData.laborEntries.reduce(
      (sum, labor) => sum + labor.totalCost,
      0
    );

    this.formData.totalPartsCost = totalPartsCost;
    this.formData.totalLaborCost = totalLaborCost;
    this.formData.totalCost = totalPartsCost + totalLaborCost;
  };

  // Validation
  validateCurrentStep = (): boolean => {
    this.error = null;

    switch (this.activeStep) {
      case 0:
        if (!this.formData.equipmentId) {
          this.error = "Please select equipment";
          return false;
        }
        if (!this.formData.assignedTechnicianId) {
          this.error = "Please assign a technician";
          return false;
        }
        if (!this.formData.scheduledDate) {
          this.error = "Please select a scheduled date";
          return false;
        }
        return true;

      case 1:
        if (!this.formData.workDescription.trim()) {
          this.error = "Please provide a work description";
          return false;
        }
        if (this.formData.maintenanceType === "emergency" && this.formData.priority === "low") {
          this.error = "Emergency maintenance cannot have low priority";
          return false;
        }
        return true;

      case 2:
        // Parts and labor can be empty for some maintenance types
        const hasPartsOrLabor = this.formData.partsUsed.length > 0 || this.formData.laborEntries.length > 0;
        if (!hasPartsOrLabor) {
          this.error = "Please add at least one part or labor entry";
          return false;
        }
        return true;

      case 3:
        return true;

      default:
        return true;
    }
  };

  // API Actions
  loadInitialData = async () => {
    this.loading = true;
    this.error = null;

    try {
      // Load equipment
      if (this.rootStore.equipmentStore) {
        await this.rootStore.equipmentStore.find({
          query: {
            $limit: 100,
            active: true,
          },
        });
      }

      // Load technicians/users
      if (this.rootStore.userStore) {
        await this.rootStore.userStore.find({
          query: {
            $limit: 100,
            active: true,
            role: "technician",
          },
        });
      }

      // Load maintenance types
      if (this.rootStore.maintenanceTypeStore) {
        await this.rootStore.maintenanceTypeStore.find({
          query: {
            $limit: 50,
            active: true,
          },
        });
      }

      // Load parts inventory
      if (this.rootStore.partStore) {
        await this.rootStore.partStore.find({
          query: {
            $limit: 500,
            active: true,
          },
        });
      }

      runInAction(() => {
        this.maintenanceTypes = this.rootStore.maintenanceTypeStore?.entities || [];
        this.technicians = this.rootStore.userStore?.entities.filter(u => u.role === "technician") || [];
        this.loading = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || "Failed to load initial data";
        this.loading = false;
      });
    }
  };

  submitMaintenanceWork = async () => {
    if (!this.validateCurrentStep()) {
      return;
    }

    this.loading = true;
    this.error = null;

    try {
      // Prepare maintenance record
      const maintenanceData = {
        ...this.formData,
        submittedAt: new Date().toISOString(),
        submittedBy: this.rootStore.authStore?.user?.id,
        actualStartDate: new Date().toISOString(),
        status: "completed",
        completedBy: this.rootStore.authStore?.user?.id,
        completedDate: new Date().toISOString(),
      };

      // API call would go here
      await new Promise((resolve) => setTimeout(resolve, 1000)); // Simulated API call

      runInAction(() => {
        this.success = "Equipment maintenance completed successfully!";
        this.loading = false;

        // Reset form after successful submission
        setTimeout(() => {
          this.reset();
          this.rootStore.routerStore.goTo("Dashboard");
        }, 2000);
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || "Failed to submit maintenance record";
        this.loading = false;
      });
    }
  };

  // Computed Values
  get equipment() {
    return this.rootStore.equipmentStore?.entities || [];
  }

  get parts() {
    return this.rootStore.partStore?.entities || [];
  }

  get selectedEquipment() {
    return this.equipment.find((e) => e.id === this.formData.equipmentId);
  }

  get selectedTechnician() {
    return this.technicians.find((t) => t.id === this.formData.assignedTechnicianId);
  }

  get costSummary(): MaintenanceSummary[] {
    const summary: MaintenanceSummary[] = [];

    // Add parts summary
    this.formData.partsUsed.forEach((part) => {
      summary.push({
        category: part.partName,
        description: `${part.quantity} x ${part.partNumber}`,
        cost: part.totalCost,
        type: "parts",
      });
    });

    // Add labor summary
    this.formData.laborEntries.forEach((labor) => {
      summary.push({
        category: labor.technicianName,
        description: `${labor.hours} hours - ${labor.description}`,
        cost: labor.totalCost,
        type: "labor",
      });
    });

    return summary;
  }

  get maintenanceHistory() {
    if (!this.selectedEquipment) return [];

    // This would typically come from the API
    return this.selectedEquipment.maintenanceHistory || [];
  }

  // Reset
  reset = () => {
    this.activeStep = 0;
    this.loading = false;
    this.error = null;
    this.success = null;
    this.formData = {
      equipmentId: "",
      maintenanceType: "preventive",
      priority: "medium",
      scheduledDate: new Date().toISOString().split("T")[0],
      assignedTechnicianId: "",
      workDescription: "",
      partsUsed: [],
      laborEntries: [],
      totalPartsCost: 0,
      totalLaborCost: 0,
      totalCost: 0,
      status: "scheduled",
    };
  };
}

export default EquipmentMaintenanceWizardStore;